import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type SalesforceLeadsService from 'garaje/services/salesforce-leads';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface EnterpriseDialogSignature {
  Args: {
    closeEnterpriseDialog: () => void;
    displayRequestSentDialog: () => void;
    description: string;
    header: string;
  };
}

export default class EnterpriseDialog extends Component<EnterpriseDialogSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare salesforceLeads: SalesforceLeadsService;
  @service declare metrics: MetricsService;

  createSalesforceLeadTask = dropTask(async () => {
    this.requestContactClicked();

    try {
      await this.salesforceLeads.createLead({
        email: this.currentAdmin.email,
        name: this.currentAdmin.fullName,
        leadSource: 'Custom Roles Enterprise Upgrade',
      });
      this.args.displayRequestSentDialog();
    } catch (e) {
      this.flashMessages.showFlash('error', 'Try again', parseErrorForDisplay(e));
    }
  });

  requestContactClicked(): void {
    this.metrics.trackEvent('CTA Clicked', {
      cta_id: 'custom-roles-enterprise-upgrade-modal',
      cta_type: 'modal',
      cta_clickable_type: 'button',
      cta_intent: 'upgrade',
      cta_clickable_text: 'Request to be contacted',
    });
  }

  <template>
    <ModalDialog
      @containerClass="ember-modal-dialog--enterprise-dialog !w-[600px] !max-w-[600px]"
      @overlayClassNames="ember-modal-overlay--fadeIn"
      @targetAttachment="center"
      @translucentOverlay={{true}}
      @onClose={{@closeEnterpriseDialog}}
    >
      <div class="m-8 relative">
        <h2 class="mb-2">{{@header}}</h2>

        <button
          type="button"
          class="btn btn--small btn--outline btn--cancel absolute right-0 top-0"
          {{on "click" @closeEnterpriseDialog}}
        >
          <span aria-hidden="true">&times;</span>
          <span class="sr-only">Close</span>
        </button>

        <p class="mt-4 font-normal text-carbon-50 mb-8">
          {{@description}}
        </p>

        <img src="/assets/images/features/paywall.svg" alt="upgrade to enterprise graphic" />

        <div class="mt-8">
          <button
            data-test-request-contact-btn
            type="button"
            class="btn btn--small btn--brand w-full mb-4"
            {{on "click" this.createSalesforceLeadTask.perform}}
          >
            Request to be contacted
          </button>
          <button type="button" class="btn btn--small btn--link w-full" {{on "click" @closeEnterpriseDialog}}>
            Maybe later
          </button>
        </div>
      </div>
    </ModalDialog>
  </template>
}
